import { feature } from 'topojson-client';

// 从 world-atlas TopoJSON 取国家轮廓,投影成 SVG path。
// 简易等距圆柱投影 + 按中心纬度做经度收缩(cosφ)以减轻高纬拉伸,足够用于「看轮廓认国家」。

let topoPromise: Promise<any> | null = null;
function loadTopo(): Promise<any> {
  if (!topoPromise) {
    // 50m(而非 110m):110m 下德国只有 58 个顶点,轮廓粗糙得像多边形积木。
    // 50m 约 10 倍细节(德国 561 点),gzip +193KB —— 形状本身就是玩法主体,值这个代价。
    // 地球组件仍用 110m:420px 的小球看不出差别,不必给列表页加载。
    topoPromise = fetch('/assets/geo/countries-50m.json').then((r) => r.json());
  }
  return topoPromise;
}

type Ring = number[][];
type Box = [number, number, number, number];

const ringBox = (r: Ring): Box =>
  r.reduce<Box>(
    (b, [x, y]) => [Math.min(b[0], x), Math.min(b[1], y), Math.max(b[2], x), Math.max(b[3], y)],
    [Infinity, Infinity, -Infinity, -Infinity],
  );
const ringArea = (r: Ring): number => {
  let a = 0;
  for (let i = 0, j = r.length - 1; i < r.length; j = i++) a += (r[j][0] + r[i][0]) * (r[j][1] - r[i][1]);
  return Math.abs(a / 2);
};
// 两个包围盒之间的最短间隙(0 = 相接/重叠)
const boxGap = (a: Box, b: Box): number =>
  Math.hypot(Math.max(0, a[0] - b[2], b[0] - a[2]), Math.max(0, a[1] - b[3], b[1] - a[3]));

/**
 * 只保留「主陆块集群」:从最大的环出发做单链聚类,不断吸收与集群内任一环足够近的环。
 * 岛链(印尼/菲律宾/巴哈马)会整条连通保留,东西马来西亚也保住;
 * 而法属圭亚那这类孤悬海外的飞地会被甩掉 —— 否则包围盒跨洋,本土被压缩到无法辨认。
 * 阈值 0.6×主陆块对角(下限 1.5°):实测 165 个有轮廓的国家中仅 France 被改动。
 */
/**
 * 跨 180° 经线归一化:俄罗斯(楚科奇)、斐济这类国家横跨反子午线,
 * 东端经度会从 +180 跳到 -180,原始跨度接近 360° —— 形状被压成一条横线。
 * 把负经度整体 +360 若能缩小跨度,则采用,使几何连续。
 */
function unwrapAntimeridian(rings: Ring[]): Ring[] {
  let min = Infinity;
  let max = -Infinity;
  for (const r of rings)
    for (const [lon] of r) {
      if (lon < min) min = lon;
      if (lon > max) max = lon;
    }
  if (max - min <= 180) return rings;

  let min2 = Infinity;
  let max2 = -Infinity;
  for (const r of rings)
    for (const [lon] of r) {
      const l = lon < 0 ? lon + 360 : lon;
      if (l < min2) min2 = l;
      if (l > max2) max2 = l;
    }
  if (max2 - min2 >= max - min) return rings; // 没改善(如整体位于西半球)则保持原样
  return rings.map((r) => r.map(([lon, lat]) => [lon < 0 ? lon + 360 : lon, lat]));
}

function mainCluster(rings: Ring[]): Ring[] {
  if (rings.length < 2) return rings;
  const info = rings
    .map((r) => ({ r, a: ringArea(r), b: ringBox(r) }))
    .sort((x, y) => y.a - x.a);
  const mb = info[0].b;
  const thr = Math.max(Math.hypot(mb[2] - mb[0], mb[3] - mb[1]) * 0.6, 1.5);
  const keep = [info[0]];
  const rest = info.slice(1);
  for (let grew = true; grew; ) {
    grew = false;
    for (let i = rest.length - 1; i >= 0; i--) {
      if (keep.some((k) => boxGap(rest[i].b, k.b) < thr)) {
        keep.push(rest[i]);
        rest.splice(i, 1);
        grew = true;
      }
    }
  }
  return keep.map((x) => x.r);
}

/**
 * 主陆块轮廓,投影并缩放到 W×H 画布内的像素环。
 * Outline 用它拼 SVG path;Draw 用它做形状比对(两者必须同一套几何,否则评分对不上谜面)。
 */
export async function getCountryRings(
  ccn3: string,
  W: number,
  H: number,
  pad = 12,
): Promise<Ring[] | null> {
  const topo = await loadTopo();
  const fc = feature(topo, topo.objects.countries);
  const f = fc.features.find((x: any) => Number(x.id) === Number(ccn3));
  if (!f) return null;

  const polys: number[][][][] =
    f.geometry.type === 'Polygon' ? [f.geometry.coordinates] : f.geometry.coordinates;

  const allRings: Ring[] = [];
  for (const poly of polys) for (const ring of poly) if (ring.length >= 4) allRings.push(ring);
  // 先解跨经线,再聚类 —— 聚类靠包围盒间隙判断,跨经线会让它误判为「相隔半个地球」
  const rings = mainCluster(unwrapAntimeridian(allRings));

  let sumLat = 0;
  let n = 0;
  for (const ring of rings)
    for (const [, lat] of ring) {
      sumLat += lat;
      n++;
    }
  if (!n) return null;
  const lat0 = ((sumLat / n) * Math.PI) / 180;
  const kx = Math.max(Math.cos(lat0), 0.2);
  const project = (lon: number, lat: number): [number, number] => [lon * kx, -lat];

  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  for (const ring of rings)
    for (const [lon, lat] of ring) {
      const [x, y] = project(lon, lat);
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
    }

  const bw = maxX - minX || 1;
  const bh = maxY - minY || 1;
  const scale = Math.min((W - 2 * pad) / bw, (H - 2 * pad) / bh);
  const ox = (W - bw * scale) / 2 - minX * scale;
  const oy = (H - bh * scale) / 2 - minY * scale;

  return rings.map((ring) =>
    ring.map(([lon, lat]) => {
      const [px, py] = project(lon, lat);
      return [px * scale + ox, py * scale + oy];
    }),
  );
}

export async function getCountryPath(
  ccn3: string,
  W: number,
  H: number,
  pad = 12,
): Promise<string | null> {
  const rings = await getCountryRings(ccn3, W, H, pad);
  if (!rings) return null;
  let d = '';
  for (const ring of rings) {
    ring.forEach(([x, y], i) => {
      d += (i === 0 ? 'M' : 'L') + x.toFixed(1) + ' ' + y.toFixed(1);
    });
    d += 'Z';
  }
  return d;
}
